import { FunctionComponent, useMemo } from 'react';
import './styles/DashboardTopNavigationBar.scss';

import { Link } from 'react-router-dom';

import { Badge } from '@mui/material';
import { CalendarMonth, Menu as MenuIcon, Person } from '@mui/icons-material';

import { useActiveEnrolmentYear, useAuth, useConfirm, useScreenSize } from '@dw/hooks';
import { useGetMyPendingDanceSchoolInvites } from '@dw/hooks/api/DanceSchoolUserApi';
import { Avatar, Button, Chip, HStack } from '@dw/components/core';
import { getSchoolMarkKey } from '@dw/models/danceSchool';
import { IMenuItem } from '@dw/models/shared';
import { classNames, componentClassName } from '@dw/utils';
import { SCREEN_SIZE } from '@dw/constants';
import { dayjs } from '@dw/dayjs';

interface IDashboardTopNavigationBarProps {
    menuItems: IMenuItem[];
    menuItemSelected: (menuItem: IMenuItem) => void;
    isLoading: boolean;
}
export const DashboardTopNavigationBar: FunctionComponent<IDashboardTopNavigationBarProps> = (props: IDashboardTopNavigationBarProps) => {

    const { menuItems, menuItemSelected, isLoading } = props;

    const { user, danceSchool } = useAuth();

    /*
      The mark for a square, falling back down the chain. Every school has the letterhead logo (it
      gates Active); the two in front of it are optional marks drawn to read at this size — the
      Square logo slot under Settings > Dancer App, which previews exactly this, and then the School
      logo slot under Settings > Your School.
    */
    const schoolMarkKey = getSchoolMarkKey(danceSchool);
    const { data: pendingInvites } = useGetMyPendingDanceSchoolInvites();

    // Same signal as the side nav's school footer, on the only school control this layout has.
    const pendingInviteCount = pendingInvites?.length ?? 0;
    const { isMobile, currentSize } = useScreenSize();
    const { confirm } = useConfirm();
    const { activeEnrolmentYear, latestEnrolmentYear, isLatestEnrolmentYearActive, setActiveEnrolmentYear } = useActiveEnrolmentYear();

    const availableEnrolmentYears = useMemo(() => {
        if (!danceSchool) {
            return [];
        }

        const availableEnrolmentYears = (danceSchool?.annualSettings ?? [])
            .sort((a, b) => b.year - a.year)
            .map(x => x.year);

        if (latestEnrolmentYear && !availableEnrolmentYears.includes(latestEnrolmentYear)) {
            availableEnrolmentYears.push(latestEnrolmentYear);
        }
        if (activeEnrolmentYear && !availableEnrolmentYears.includes(activeEnrolmentYear)) {
            availableEnrolmentYears.push(activeEnrolmentYear);
        }

        // Offer next year so the owner can configure its terms before it becomes current.
        // The API only accepts saving term dates for the current or next calendar year, so
        // never surface a year that couldn't be saved.
        const nextEnrolmentYear = latestEnrolmentYear + 1;
        if (nextEnrolmentYear <= dayjs().year() + 1 && !availableEnrolmentYears.includes(nextEnrolmentYear)) {
            availableEnrolmentYears.push(nextEnrolmentYear);
        }

        availableEnrolmentYears.sort().reverse();
        return availableEnrolmentYears;
    }, [danceSchool, activeEnrolmentYear, latestEnrolmentYear]);

    const menuItemsToUse = useMemo(() => {
        const updatedMenuItems: IMenuItem[] = [];

        if (danceSchool) {
            updatedMenuItems.push(...([
                {
                    // The count is spelled out inside the menu as well as badged outside it: the
                    // badge says something is waiting, and this says what.
                    text: pendingInviteCount > 0
                        ? `${danceSchool.name} (${pendingInviteCount} ${pendingInviteCount === 1 ? 'invite' : 'invites'})`
                        : danceSchool.name,
                    value: 'SWITCH',
                    icon: schoolMarkKey ? <Avatar size='tiny' shape='square' imageKey={schoolMarkKey} /> : undefined
                },
                { isDivider: true }
            ] as IMenuItem[]))
        }

        updatedMenuItems.push(...menuItems.filter(x => (!x.data || x.data === 'TOP_NAV') && !x.hidden));

        return updatedMenuItems;
    }, [menuItems, danceSchool, schoolMarkKey, pendingInviteCount]);

    const handleSwitchActiveEnrolmentYear = () => {
        if (!availableEnrolmentYears || availableEnrolmentYears.length < 2) {
            return;
        }

        confirm({
            title: 'Switch Enrolment Year',
            message: 'Please select the enrolment year you would like to view/manage',
            captureInputConfigs: {
                enrolmentYear: {
                    inputType: 'select',
                    label: 'Enrolment Year',
                    defaultValue: activeEnrolmentYear.toString(),
                    required: true,
                    options: availableEnrolmentYears.map(x => ({
                        value: x.toString(),
                        text: x.toString()
                    }))
                }
            },
            onPositive: (inputs) => {
                if (!inputs || !inputs.enrolmentYear) {
                    return;
                }

                const selectedEnrolmentYear = parseInt(inputs.enrolmentYear);
                if (!isNaN(selectedEnrolmentYear)) {
                    setActiveEnrolmentYear(selectedEnrolmentYear);
                    window.location.reload();
                }
            }
        })
    }

    const cn = componentClassName('dashboard-top-navigation');

    return (
        <HStack
            breakpoint={false}
            justifyContent='space-between'
            className={classNames(cn(), {
                [cn('lg')]: currentSize === SCREEN_SIZE.LG,
                [cn('md')]: currentSize === SCREEN_SIZE.MD,
                [cn('sm')]: currentSize === SCREEN_SIZE.SM,
                [cn('xs')]: currentSize === SCREEN_SIZE.XS
            })}
        >
            <Link to='/dashboard' className={cn('logo-link-wrapper')}>
                <img src='/images/logo-primary.webp' alt='Dance World' height={currentSize <= SCREEN_SIZE.XS ? 25 : currentSize <= SCREEN_SIZE.MD ? 30 : currentSize <= SCREEN_SIZE.LG ? 35 : 40} />
            </Link>

            <HStack>
                {activeEnrolmentYear && availableEnrolmentYears.length > 1 && (
                    <Chip
                        label={activeEnrolmentYear.toString()}
                        color={isLatestEnrolmentYearActive ? 'primary' : 'warning'}
                        icon={<CalendarMonth />}
                        tooltip='Click to switch to different enrolment year'
                        onClick={handleSwitchActiveEnrolmentYear}
                    />
                )}

                <Badge badgeContent={pendingInviteCount} color='error'>
                    <Button
                        startIcon={isMobile ? <MenuIcon /> : schoolMarkKey ? <Avatar size='tiny' shape='square' imageKey={schoolMarkKey} /> : <Person />}
                        variant='text'
                        color='primary'
                        size='small'
                        menuOptions={menuItemsToUse}
                        menuOptionSelected={menuItemSelected}
                        menuSx={{
                            maxHeight: '1000px !important'
                        }}
                        loading={isLoading}
                    >
                        {user?.firstName}
                    </Button>
                </Badge>
            </HStack>
        </HStack>
    )
};